import { useState, type ReactNode } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { Header } from '../header';
import { Footer } from '../footer';
import { Layout } from '../layout';
import { ExpandGroup, EXPAND_STEP_ROOMY } from '../expand';
import { CONTACT_NGO_IDS, ContactInfo } from './info';
import { ContactPanel } from './panel';
import type { Audience, NgoTab } from './audiences';

/**
 * Warms the logo strip in the left column — both halves of it, since the audience is not known here:
 * the merchant logos ride on the global stats, and the NGO ones are their own query. `prefetchQuery`,
 * not `ensureQueryData` — see the homepage loader for why a floating rejected promise here takes the
 * server process down.
 */
export function prefetchContactPage(context: { queryClient: QueryClient }) {
	context.queryClient.prefetchQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	context.queryClient.prefetchQuery(trpc.portal.getNgoLogos.queryOptions({ ngoIds: [...CONTACT_NGO_IDS] }));
}

/**
 * The Contact page, and the SHELL the four audience pages are rendered inside.
 *
 * It belongs to the parent route, so picking an audience navigates into a child without unmounting any
 * of this: the chrome, the photograph, the address card and the panel stay put and only the
 * questionnaire changes. Rendering the whole page per audience instead would tear it down and play the
 * `Expand` entrance again on every switch — the "the page refreshes" the nesting exists to avoid.
 *
 * `children` is the child route's `<Outlet />`, which carries that page's `PageSeo` — its own title,
 * description and canonical, which is the half of the audience that cannot live in the shell.
 */
export function ContactPage(props: { audience: Audience | null; tab: NgoTab; children: ReactNode }) {
	// Which audience's message was sent, rather than a bare boolean: the shell now outlives the switch,
	// so a plain `sent` would still be true after moving to a questionnaire nobody has answered yet.
	const [sentFor, setSentFor] = useState<Audience | null>(null);
	const sent = sentFor !== null && sentFor === props.audience;

	return (
		<Layout>
			<Header />
			{props.children}
			<ExpandGroup step={EXPAND_STEP_ROOMY} asChild>
				<section className="mx-auto grid max-w-[1440px] items-start gap-6 px-6 pt-4 pb-16 md:pb-24 lg:grid-cols-[793fr_559fr] xl:px-8">
					<ContactInfo audience={props.audience} sent={sent} />
					<ContactPanel
						audience={props.audience}
						tab={props.tab}
						sent={sent}
						onSent={() => setSentFor(props.audience)}
					/>
				</section>
			</ExpandGroup>
			{/* `links`, not `form`: a second contact form directly under this one would be absurd. */}
			<Footer />
		</Layout>
	);
}
